import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "My brain can't handle OOP anymore",
  "description": "",
  "published": "2012-05-07T00:00:00.000Z",
  "redirect_from": ["/blog/my-brain-cant-handle-oop-anymore/swizec/4320"],
  "categories": "Data, Firefly, Foobar, Function, Functional programming, Joss Whedon, Languages, Object-oriented programming, Programming, River Tam, Uncategorized, Visitor pattern",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Today I realized there is nothing more confusing to me than object oriented programming. Especially in Java. People _actually_ use this!?`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/196543519446409216"
      }}>{`April 29, 2012`}</a></blockquote>
    <p>{`The other week I was working on my compilers homework - the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Compiler",
        "title": "Compiler"
      }}>{`semantic analysis`}</a>{` part is an object-oriented nightmare. Something called the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Visitor_pattern",
        "title": "Visitor pattern"
      }}>{`visitor pattern`}</a>{` to traverse trees and do weird stuff.`}</p>
    <p>{`It made me want to curl up in a fetal position in the corner, rocking back and forth in a padded room, while mumbling tongues at myself. As `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/List_of_Firefly_characters",
        "title": "List of Firefly characters"
      }}>{`River Tam`}</a>{` would say before running out of the room in panic `}<em parentName="p">{`"Too crowded!"`}</em></p>
    <h2 {...{
      "id": "what-object-oriented-programming-feels-like"
    }}>{`What `}<a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Object-oriented_programming",
        "title": "Object-oriented programming"
      }}>{`OOP`}</a>{` feels like`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/05/Summer_Glau_as_River_Tam_in_Serenity_Wallpaper__yvt2-300x225.jpg",
        "alt": "Schizophrenic?",
        "title": "Schizophrenic?"
      }}></img></p>
    <p>{`In object-oriented programming everything gets muddled together. Functions are bundled with data, everything is codependent and there's no telling what a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Function_%28mathematics%29",
        "title": "Function (mathematics)"
      }}>{`function`}</a>{` might do when you call it.`}</p>
    <p>{`And that's assuming there are no side-effects outside the object monad. Otherwise, who knows, the world might explode!`}</p>
    <p>{`This creates software that is `}<em parentName="p">{`impossible`}</em>{` to understand. I used to think I could, but after a few months of functional-style programming I realized that I simply don't have the brainpower to understand object oriented code. It's too messy.`}</p>
    <p>{`When you put singletons into the picture and objects using other objects it all just gets ... let me give you a simple example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`foo: object {
   i: private integer = 0;
   add: function (a:integer) {
      i += a;
      return i;
   }
}

/* lots of code happens here, foo has been passed around, things happened */
/* foo is not a singleton though, just used a lot */

a:integer = foo.add(5);

// What is the value of a?
`}</code></pre>
    <p>{`Answering this simple question requires knowing `}<em parentName="p"><strong parentName="em">{`everything`}</strong></em>{`. The whole execution history of `}<em parentName="p">{`foo`}</em>{`. The whole codebase. You name it, you have to keep it in your head.`}</p>
    <p>{`Oh and did I mention class `}<em parentName="p">{`bar`}</em>{`? It depends on `}<em parentName="p">{`foo`}</em>{` for a lot of its stuff. Oh yeah, they got into a little friends with benefits situation last year. It all gets pretty interesting. I think `}<em parentName="p">{`bar`}</em>{` cheated on `}<em parentName="p">{`foo`}</em>{` with `}<em parentName="p"><a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/Foobar",
          "title": "Foobar"
        }}>{`baz`}</a></em>{` once, though. There's no telling how `}<em parentName="p">{`foo`}</em>{` might react to that!`}</p>
    <h2 {...{
      "id": "functional-programming"
    }}>{`Functional programming`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/05/fp1.png",
        "alt": "fp, by okeef creations",
        "title": "fp, by okeef creations"
      }}></img></p>
    <p>{`With a lot of gentle poking and prodding from `}<a parentName="p" {...{
        "href": "http://twitter.com/sbelak"
      }}>{`@sbelak`}</a>{`, I started learning `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Functional_programming",
        "title": "Functional programming"
      }}>{`functional programming`}</a>{` about two years ago.`}</p>
    <p>{`I was promised easy scalability, working with multiple processors without cost, expressive code, things that Just Work (tm) and software with less bugs that is easier to maintain and shorter/quicker to write.`}</p>
    <p>{`I may not have gotten all that, but I `}<em parentName="p">{`did`}</em>{` get a whole new way of thinking about my craft. An easier way to `}<em parentName="p">{`understand.`}</em></p>
    <p>{`At first functional programming felt like performing a lobotomy on myself by sticking hot pokers into my feet. `}<em parentName="p">{`Are you `}<strong parentName="em">{`sure`}</strong>{` I don't need loops? But I do need variables don't I? At least a bit of mutability? Really? I don't? This is some sort of hazing ritual isn't it, any moment now you're all going to jump up, shout "surprise" and start laughing at me.`}</em></p>
    <p>{`_`}{`_`}{`But I kept at it. And even though my code looked horrible, I fell in love.`}</p>
    <p>{`The first attraction of functinal style code was writing python functions that just pass data around. No storing in intermediate variables, just passing one function's result directly into the arguments of the next function in the chain.`}</p>
    <p>{`Because who cares about reading intermediate steps? All I care for is that this chunk of code creates `}<em parentName="p">{`X`}</em>{`. Not that it first does `}<em parentName="p">{`Y`}</em>{`, then `}<em parentName="p">{`Z,`}</em>{` then pulls in `}<em parentName="p">{`A`}</em>{` and `}<em parentName="p">{`B`}</em>{` and combines them into `}<em parentName="p">{`X.`}</em></p>
    <p>{`Sure, you could define a function called `}<em parentName="p">{`X`}</em>{` that combines `}<em parentName="p">{`Y, Z, A `}{`_`}{`and `}{`_`}{`B, `}{`_`}{`but why create a new function for something that only gets called once? Much better to just do something like `}{`_`}{`X = A+B(Y(Z))`}</em>{` isn't it?`}</p>
    <h2 {...{
      "id": "a-waterfall-of-data"
    }}>{`A waterfall of data`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/05/waterfall-of-data.jpg",
        "alt": "Plitvice falls",
        "title": "Plitvice falls"
      }}></img></p>
    <p>{`The way I think about code now is a waterfall of data.`}</p>
    <p>{`You have data and every function in the chain is a ledge. The data stops falling, changes course a bit, perhaps even changes some properties, and falls onward to the next function.`}</p>
    <p>{`In the end you have a pool. This is where your data ends up after falling through many functions and being changed. But then it flows onward to the next function, or wherever you might need it.`}</p>
    <p>{`You might say this is really `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Procedural_programming",
        "title": "Procedural programming"
      }}>{`procedural programming`}</a>{`, but I don't like side-effects, imperative bits of code and so on. It's still functional programming, but this is how my brain understands code. Your mileage might vary.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`Whatever way you think about your code, whatever way you visualise execution in your head ... hat's off to you, if you can handle the inherent complexity of object-oriented programming. You are a better man than I am.`}</p>
    <p>{`I think you should give "`}<em parentName="p">{`this functional programming fad"`}</em>{` a try anyway. You might like it ;)`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://restreaming.wordpress.com/2012/03/16/functional-programming-in-practice/"
        }}>{`Functional programming in practice`}</a>{` (restreaming.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/closure-and-currying-magic-for-cleaner-javascript/swizec/3981"
        }}>{`Closure and currying magic for cleaner javascript`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://teknonics.wordpress.com/2012/05/04/objects-and-php-a-brief-evolutionary-history/"
        }}>{`Objects and PHP: A brief evolutionary history`}</a>{` (teknonics.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://normansoven.com/blog/still-trying-to-wrap-my-head-around-functional-programming/"
        }}>{`Still trying to wrap my head around functional programming.`}</a>{` (normansoven.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://bulldozer00.com/2012/05/05/the-old-is-new-again/"
        }}>{`The Old Is New Again`}</a>{` (bulldozer00.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.altdevblogaday.com/2012/04/26/functional-programming-in-c/"
        }}>{`Functional Programming in C++`}</a>{` (altdevblogaday.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://slidetocode.com/2012/05/06/the-perfect-programming-language/"
        }}>{`The genesis of the perfect programming language`}</a>{` (slidetocode.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=6209c6cc-944f-4b49-b45c-5467e35073f2",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      